import { useState } from 'react'
import { useNotasDaGmud, useRegistrarNota } from '../../../api/queries/gmuds'
import { Botao } from '../../../components/Botao'
import { AvisoDeErro, Esqueleto } from '../../../components/Estados'
import { Cartao } from '../../../components/ListaDeDados'
import { CampoDeTextoLongo } from '../../../components/campos'
import { dataHora } from '../../../lib/datas'
import { TEXTOS } from '../../../lib/glossario'

/**
 * As observações registradas na solicitação.
 *
 * **O rito do comitê é presencial** — reunião, pauta e ata acontecem no escritório. O que o sistema
 * guarda é a análise e o que se observou nela: por que o comitê descartou uma ressalva do parecer
 * automático, o que a revisão de código mostrou, o combinado da reunião.
 *
 * **Nota não é decisão**, e o texto da tela diz isso: a decisão é a ação com fundamentação, acima.
 * Escrever "aprovado" numa nota não move a solicitação — é a máquina de estados que decide, e ela
 * não passa por aqui.
 *
 * Registrada, a observação não se edita nem se apaga: o que sustentou uma decisão não se reescreve
 * depois dela. A guarda é do banco, não da tela.
 *
 * @param props a solicitação
 * @returns o cartão de observações
 */
export function NotasDaGmud({ gmudId }: { gmudId: number }) {
  const notas = useNotasDaGmud(gmudId)
  const registrar = useRegistrarNota(gmudId)
  const [texto, setTexto] = useState('')

  async function salvar() {
    try {
      await registrar.mutateAsync(texto)
      setTexto('')
    } catch {
      // O AvisoDeErro abaixo diz o que o servidor recusou e por quê.
    }
  }

  return (
    <Cartao titulo={TEXTOS.notas.titulo} descricao={TEXTOS.notas.ajuda}>
      <div className="space-y-4">
        <div className="space-y-2">
          <CampoDeTextoLongo
            rotulo={TEXTOS.notas.campo}
            ajuda={TEXTOS.notas.ajudaDoCampo}
            rows={3}
            value={texto}
            onChange={(evento) => setTexto(evento.target.value)}
          />
          {registrar.isError && <AvisoDeErro erro={registrar.error} titulo={TEXTOS.notas.naoSalvou} />}
          <Botao aparencia="primario" onClick={salvar} disabled={registrar.isPending || texto.trim() === ''}>
            {registrar.isPending ? TEXTOS.notas.salvando : TEXTOS.notas.salvar}
          </Botao>
        </div>

        {notas.isPending && <Esqueleto linhas={3} />}
        {notas.isError && <AvisoDeErro erro={notas.error} titulo="Não foi possível carregar as observações" />}
        {notas.data?.length === 0 && <p className="text-sm text-slate-500">{TEXTOS.notas.vazio}</p>}

        {notas.data && notas.data.length > 0 && (
          <ul className="space-y-3">
            {notas.data.map((nota) => (
              <li key={nota.id} className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                <p className="whitespace-pre-line text-sm text-slate-800">{nota.texto}</p>
                <p className="mt-1 text-xs text-slate-500">
                  {nota.autorNome ?? 'Autor não informado'} · {dataHora(nota.criadoEm)}
                </p>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Cartao>
  )
}
